import React, { useState, useEffect } from 'react';
import { TrendingUp, Users, Layers, ShieldHalf, Activity } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/Card';
import { Badge } from './ui/Badge';
import dashboardService from '../services/dashboard';

const Analytics = () => {
    const [loading, setLoading] = useState(true);
    const [analytics, setAnalytics] = useState({
        userGrowth: [],
        appUsage: [],
        roleDistribution: [],
        activeSessions: [],
        userActivityByApp: [],
    });

    useEffect(() => {
        loadAnalytics();
    }, []);

    const loadAnalytics = async () => {
        setLoading(true);
        try {
            const data = await dashboardService.getAnalytics();
            if (data) {
                setAnalytics({
                    userGrowth: data.userGrowth || [],
                    appUsage: data.appUsage || [],
                    roleDistribution: data.roleDistribution || [],
                    activeSessions: data.activeSessions || [],
                    userActivityByApp: data.userActivityByApp || [],
                });
            }
        } catch (err) {
            console.error('Error loading analytics:', err);
        } finally {
            setLoading(false);
        }
    };

    const formatMonth = (monthStr) => {
        if (!monthStr) return '';
        const [year, month] = monthStr.split('-');
        const date = new Date(year, month - 1);
        return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    };

    const getMaxValue = (data, key) => {
        if (!data || data.length === 0) return 1;
        return Math.max(...data.map((item) => parseInt(item[key]) || 0), 1);
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="grid gap-6 md:grid-cols-2">
                    {[1, 2, 3, 4].map((i) => (
                        <Card key={i}>
                            <CardContent className="p-6">
                                <div className="h-64 bg-slate-200 rounded animate-pulse" />
                            </CardContent>
                        </Card>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* User Growth Trend */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <TrendingUp className="h-5 w-5" />
                        User Growth Trend (Last 12 Months)
                    </CardTitle>
                    <CardDescription>New user registrations over time</CardDescription>
                </CardHeader>
                <CardContent>
                    {analytics.userGrowth.length === 0 ? (
                        <p className="text-sm text-slate-500 text-center py-8">No data available</p>
                    ) : (
                        <div className="space-y-4">
                            <div className="flex items-end gap-2 h-48">
                                {analytics.userGrowth.map((item, index) => {
                                    const maxValue = getMaxValue(analytics.userGrowth, 'user_count');
                                    const height = (item.user_count / maxValue) * 100;
                                    return (
                                        <div key={index} className="flex-1 flex flex-col items-center gap-2">
                                            <div className="relative w-full h-full flex items-end">
                                                <div
                                                    className="w-full bg-primary-600 rounded-t hover:bg-primary-700 transition-colors"
                                                    style={{ height: `${height}%` }}
                                                    title={`${item.user_count} users`}
                                                />
                                            </div>
                                            <span className="text-xs text-slate-600 transform -rotate-45 origin-top-left whitespace-nowrap">
                                                {formatMonth(item.month)}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                                <div>
                                    <p className="text-xs text-slate-500">Total Users</p>
                                    <p className="text-lg font-semibold">
                                        {analytics.userGrowth.reduce(
                                            (sum, item) => sum + (parseInt(item.user_count) || 0),
                                            0
                                        )}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500">Average/Month</p>
                                    <p className="text-lg font-semibold">
                                        {analytics.userGrowth.length > 0
                                            ? Math.round(
                                                  analytics.userGrowth.reduce(
                                                      (sum, item) => sum + (parseInt(item.user_count) || 0),
                                                      0
                                                  ) / analytics.userGrowth.length
                                              )
                                            : 0}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500">Peak Month</p>
                                    <p className="text-lg font-semibold">
                                        {analytics.userGrowth.length > 0
                                            ? Math.max(
                                                  ...analytics.userGrowth.map((item) => parseInt(item.user_count) || 0)
                                              )
                                            : 0}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* App Usage Trend */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Layers className="h-5 w-5" />
                        App Usage Trend (Last 12 Months)
                    </CardTitle>
                    <CardDescription>User assignments to applications over time</CardDescription>
                </CardHeader>
                <CardContent>
                    {analytics.appUsage.length === 0 ? (
                        <p className="text-sm text-slate-500 text-center py-8">No data available</p>
                    ) : (
                        <div className="space-y-4">
                            <div className="flex items-end gap-2 h-48">
                                {analytics.appUsage.map((item, index) => {
                                    const maxValue = getMaxValue(analytics.appUsage, 'user_count');
                                    const height = (item.user_count / maxValue) * 100;
                                    return (
                                        <div key={index} className="flex-1 flex flex-col items-center gap-2">
                                            <div className="relative w-full h-full flex items-end">
                                                <div
                                                    className="w-full bg-emerald-600 rounded-t hover:bg-emerald-700 transition-colors"
                                                    style={{ height: `${height}%` }}
                                                    title={`${item.user_count} users`}
                                                />
                                            </div>
                                            <span className="text-xs text-slate-600 transform -rotate-45 origin-top-left whitespace-nowrap">
                                                {formatMonth(item.month)}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                                <div>
                                    <p className="text-xs text-slate-500">Total Assignments</p>
                                    <p className="text-lg font-semibold">
                                        {analytics.appUsage.reduce(
                                            (sum, item) => sum + (parseInt(item.user_count) || 0),
                                            0
                                        )}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500">Unique Apps</p>
                                    <p className="text-lg font-semibold">
                                        {new Set(analytics.appUsage.map((item) => item.app_count)).size}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            <div className="grid gap-6 md:grid-cols-2">
                {/* Role Distribution */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <ShieldHalf className="h-5 w-5" />
                            Top Role Distribution
                        </CardTitle>
                        <CardDescription>Most assigned roles across applications</CardDescription>
                    </CardHeader>
                    <CardContent>
                        {analytics.roleDistribution.length === 0 ? (
                            <p className="text-sm text-slate-500 text-center py-8">No data available</p>
                        ) : (
                            <div className="space-y-4">
                                {analytics.roleDistribution.map((role, index) => {
                                    const maxValue = getMaxValue(analytics.roleDistribution, 'user_count');
                                    const width = (role.user_count / maxValue) * 100;
                                    return (
                                        <div key={index} className="space-y-2">
                                            <div className="flex items-center justify-between text-sm">
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-medium text-slate-900 break-words">
                                                        {role.role_name}
                                                    </p>
                                                    <p className="text-xs text-slate-500 break-words">
                                                        {role.app_name}
                                                    </p>
                                                </div>
                                                <Badge variant="secondary" className="ml-2">
                                                    {role.user_count} users
                                                </Badge>
                                            </div>
                                            <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                                                <div
                                                    className="h-full bg-primary-600 rounded-full transition-all"
                                                    style={{ width: `${width}%` }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Active Sessions Trend */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Activity className="h-5 w-5" />
                            Active Sessions (Last 30 Days)
                        </CardTitle>
                        <CardDescription>Daily session creation trends</CardDescription>
                    </CardHeader>
                    <CardContent>
                        {analytics.activeSessions.length === 0 ? (
                            <p className="text-sm text-slate-500 text-center py-8">No data available</p>
                        ) : (
                            <div className="space-y-4">
                                <div className="flex items-end gap-1 h-48">
                                    {analytics.activeSessions.slice(-14).map((item, index) => {
                                        const maxValue = getMaxValue(analytics.activeSessions, 'session_count');
                                        const height = (item.session_count / maxValue) * 100;
                                        return (
                                            <div key={index} className="flex-1 flex flex-col items-center gap-1">
                                                <div className="relative w-full h-full flex items-end">
                                                    <div
                                                        className="w-full bg-blue-600 rounded-t hover:bg-blue-700 transition-colors"
                                                        style={{ height: `${height}%` }}
                                                        title={`${item.session_count} sessions`}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                                    <div>
                                        <p className="text-xs text-slate-500">Total Sessions</p>
                                        <p className="text-lg font-semibold">
                                            {analytics.activeSessions.reduce(
                                                (sum, item) => sum + (parseInt(item.session_count) || 0),
                                                0
                                            )}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-500">Avg/Day</p>
                                        <p className="text-lg font-semibold">
                                            {analytics.activeSessions.length > 0
                                                ? Math.round(
                                                      analytics.activeSessions.reduce(
                                                          (sum, item) => sum + (parseInt(item.session_count) || 0),
                                                          0
                                                      ) / analytics.activeSessions.length
                                                  )
                                                : 0}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* User Activity by App */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Users className="h-5 w-5" />
                        User Activity by Application
                    </CardTitle>
                    <CardDescription>User engagement metrics per application</CardDescription>
                </CardHeader>
                <CardContent>
                    {analytics.userActivityByApp.length === 0 ? (
                        <p className="text-sm text-slate-500 text-center py-8">No data available</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b">
                                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-700">
                                            Application
                                        </th>
                                        <th className="text-right py-3 px-4 text-sm font-medium text-slate-700">
                                            Total Users
                                        </th>
                                        <th className="text-right py-3 px-4 text-sm font-medium text-slate-700">
                                            New (30d)
                                        </th>
                                        <th className="text-right py-3 px-4 text-sm font-medium text-slate-700">
                                            Active (30d)
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {analytics.userActivityByApp.map((app, index) => (
                                        <tr key={index} className="border-b hover:bg-slate-50 transition-colors">
                                            <td className="py-3 px-4">
                                                <div className="font-medium text-slate-900">{app.app_name}</div>
                                            </td>
                                            <td className="py-3 px-4 text-right">
                                                <Badge variant="default">{app.total_users || 0}</Badge>
                                            </td>
                                            <td className="py-3 px-4 text-right">
                                                <Badge variant="secondary">{app.new_users_30d || 0}</Badge>
                                            </td>
                                            <td className="py-3 px-4 text-right">
                                                <Badge variant="outline">{app.active_users_30d || 0}</Badge>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default Analytics;
